import type {
    FileInput,
    FormCheckbox,
    FormControl,
    FormInput,
    FormInputDate,
    FormRadio,
    FormSelect,
    FormTextarea,
    FormNumber,
    FormControlType,
} from '@common/types/form-element.type';

import {
    ECrmCommentPlace,
    EFormControlType,
    EFormInputType,
    EHtmlAutocompleteAttr,
    EHtmlInputType,
    ESystemAddressBookVariables,
} from '@common/enums';
import { controlsLabels } from '@common/constants/form-element-label.const';

const baseElement: FormControl = {
    label: '',
    protected: false,
    required: false,
    isDynamicPlaceholderEnabled: false,
    variable: null,
    crmContactVariable: null,
    crmDealVariable: null,
    telegramVariable: null,
    instagramVariable: null,
    viberVariable: null,
    messengerVariable: null,
    whatsappVariable: null,
    tiktokVariable: null,
    value: '',
};

const inputElement: FormInput = {
    ...baseElement,

    type: EFormInputType.input,
    htmlInputType: EHtmlInputType.text,
    autocompleteType: EHtmlAutocompleteAttr.off,
    label: controlsLabels.input,
    placeholder: 'control_input_placeholder',
};

const hiddenElement: FormInput = {
    ...inputElement,
    type: EFormInputType.hidden,
    label: controlsLabels.hidden,
    placeholder: controlsLabels.hidden,
    htmlInputType: EHtmlInputType.hidden,
};

const emailElement: FormInput = {
    ...inputElement,

    type: EFormInputType.email,
    htmlInputType: EHtmlInputType.email,
    autocompleteType: EHtmlAutocompleteAttr.email,
    label: controlsLabels.email,
    placeholder: 'username@gmail.com',
    variable: ESystemAddressBookVariables.email,
    required: true,
    protected: true,
};

const nameElement: FormInput = {
    ...inputElement,

    type: EFormInputType.name,
    autocompleteType: EHtmlAutocompleteAttr.name,
    label: controlsLabels.name,
    placeholder: 'form_default_input_name_placeholder',
};

const phoneElement: FormInput = {
    ...inputElement,

    required: true,
    type: EFormInputType.phone,
    htmlInputType: EHtmlInputType.tel,
    autocompleteType: EHtmlAutocompleteAttr.tel,
    label: controlsLabels.phone,
    placeholder: '1234567890',
    variable: ESystemAddressBookVariables.phone,
    protected: true,
};

const addressElement: FormInput = {
    ...inputElement,

    type: EFormInputType.address,
    autocompleteType: EHtmlAutocompleteAttr.streetAddress,
    label: controlsLabels.address,
    placeholder: 'form_default_input_address_placeholder',
};

const dateElement: FormInputDate = {
    ...inputElement,

    type: EFormInputType.date,
    htmlInputType: EHtmlInputType.date,
    autocompleteType: EHtmlAutocompleteAttr.off,
    label: controlsLabels.date,
};

const urlElement: FormInput = {
    ...inputElement,

    type: EFormInputType.url,
    htmlInputType: EHtmlInputType.url,
    autocompleteType: EHtmlAutocompleteAttr.url,
    label: controlsLabels.url,
    placeholder: 'https://example.com',
};

const checkboxElement: FormCheckbox = {
    ...baseElement,

    type: EFormControlType.checkbox,
    htmlInputType: EHtmlInputType.checkbox,
    label: controlsLabels.checkbox,
};

const radioElement: FormRadio = {
    ...baseElement,

    type: EFormControlType.radio,
    htmlInputType: EHtmlInputType.radio,
    label: controlsLabels.radio,
    options: [],
};

const selectElement: FormSelect = {
    ...baseElement,

    type: EFormControlType.select,
    htmlInputType: EHtmlInputType.select,
    label: controlsLabels.select,
    options: [],
};

const textareaElement: FormTextarea = {
    ...baseElement,

    crmCommentPlace: ECrmCommentPlace.crmContactComment,
    type: EFormControlType.textarea,
    autocompleteType: EHtmlAutocompleteAttr.off,
    label: controlsLabels.textarea,
    placeholder: 'form_default_textarea_placeholder',
};

const gdprConfirmElement: FormCheckbox = {
    ...checkboxElement,

    variable: ESystemAddressBookVariables.gdprConfirm,
    label: 'Yes, please keep me updated on news, events and offers',
};

const gdprTermsElement: FormCheckbox = {
    ...checkboxElement,

    variable: ESystemAddressBookVariables.gdprTerms,

    label: 'By signing up, you agree to our <a href="" target="_blank">Terms of Use and Privacy Policy</a>, including the use of cookies and transfer of your personal information to ',
};

const fileElement: FileInput = {
    ...baseElement,

    type: EFormInputType.file,
    label: controlsLabels.file,
    htmlInputType: EHtmlInputType.file,
    variable: null,
    placeholder: 'control_file_input_placeholder',
};

const numberElement: FormNumber = {
    ...inputElement,

    type: EFormInputType.number,
    autocompleteType: EHtmlAutocompleteAttr.off,
    label: controlsLabels.number,
    htmlInputType: EHtmlInputType.number,
};

const elements: Partial<Record<FormControlType, any>> = {
    [EFormInputType.input]: inputElement,
    [EFormControlType.textarea]: textareaElement,
    [EFormInputType.name]: nameElement,
    [EFormInputType.email]: emailElement,
    [EFormInputType.phone]: phoneElement,
    [EFormInputType.address]: addressElement,
    [EFormInputType.date]: dateElement,
    [EFormInputType.url]: urlElement,
    [EFormControlType.checkbox]: checkboxElement,
    [EFormControlType.radio]: radioElement,
    [EFormControlType.select]: selectElement,
    [EFormInputType.hidden]: hiddenElement,
    [EFormInputType.file]: fileElement,
    [EFormInputType.number]: numberElement,
};

export const gdprElements = {
    gdprConfirm: gdprConfirmElement,
    gdprTerms: gdprTermsElement,
};

export const defaultFormElements = new Map(Object.entries(elements)); // using map to preserve order
